import { type FC, type ReactNode, useMemo, useCallback } from 'react';
import classNames from 'classnames';

import { useInternalScrollCarousel } from '../helpers/InternalScrollCarouselContext';
import { ScrollCarouselItemProvider } from '../helpers/ScrollCarouselItemContext';
import { getGapClassName } from '../helpers/gapStyles/getGapClassName';
import { adjustFlexBasis } from '../helpers/gapStyles/adjustFlexBasis';

import styles from './CarouselItem.scss';

export const CarouselItem: FC<{
    children: ReactNode;
    //is used to determine the snap position of the item
    snapIndex: number;
    //is used to determine the index of the item
    trueIndex: number;
}> = ({ children, snapIndex, trueIndex }) => {
    const {
        itemsToShow,
        stepSize,
        totalItems,
        isEndSnapScroll,
        currentIndex,
        setCurrentIndex,
        hasMultipleSlides,
        gapSize,
        scrollPadding,
        carouselOrientation,
        snapAlign,
        shouldAdjustFlexBasis,
        animation,
    } = useInternalScrollCarousel();

    const endIndex = totalItems - 1;
    const lastIndexForStartSnap = endIndex - (totalItems % stepSize);

    /**
     * On full page scroll carousels don't apply start snap on last item if the page is not full. This ensures
     * better overall scroll experience.
     */
    const willApplyEndSnap = isEndSnapScroll && snapIndex > lastIndexForStartSnap;

    const setActive = useCallback(() => {
        setCurrentIndex(trueIndex);
    }, [setCurrentIndex, trueIndex]);

    const contextValue = useMemo(
        () => ({
            isActive:
                trueIndex === currentIndex ||
                //if carousel doesn't have enough items to fill the page, but sync is enabled, we need to adjust the active item
                (!hasMultipleSlides && trueIndex === currentIndex - totalItems),
            setActive,
        }),
        [trueIndex, currentIndex, setActive, hasMultipleSlides, totalItems]
    );

    const useStrictStop = animation === 'slide-strict';
    const isStartSnap = !willApplyEndSnap && snapIndex % stepSize === 0;
    const isEndSnap = willApplyEndSnap && snapIndex === endIndex;

    const snapClasses = useMemo(() => {
        if (snapAlign === 'center') {
            return {
                [styles.carouselSnapItemCenter]: true,
            };
        }

        return {
            [styles.carouselSnapItemStart]: isStartSnap,
            [styles.carouselSnapItemEnd]: isEndSnap,
        };
    }, [snapAlign, isStartSnap, isEndSnap]);

    return (
        <ScrollCarouselItemProvider value={contextValue}>
            <div
                className={classNames(
                    styles.carouselItem,
                    styles[getGapClassName({ gapSize, orientation: carouselOrientation })],
                    {
                        [styles.carouselItemSlideStrict]: useStrictStop,
                    },
                    snapClasses
                )}
                style={
                    shouldAdjustFlexBasis
                        ? {
                              flexBasis: adjustFlexBasis({
                                  scrollPadding,
                                  gapSize,
                                  itemsToShow,
                              }),
                          }
                        : undefined
                }
            >
                {children}
            </div>
        </ScrollCarouselItemProvider>
    );
};
